import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  me, logout, updateSiteContent, listBookings, deleteBooking,
  formatErrorDetail, getToken,
} from "@/lib/adminApi";
import { useSiteContent } from "@/lib/siteContent";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ADMIN } from "@/constants/testIds";
import { LogOut, Trash2, ExternalLink, Download } from "lucide-react";
import {
  Field, TextField, TextAreaField, ImageField,
  StringListEditor, ParagraphListEditor, ObjectListEditor, SaveBar,
  inputCls,
} from "@/components/admin/Fields";
import GalleryEditor from "@/components/admin/GalleryEditor";
import { Input } from "@/components/ui/input";
import { adminAxios } from "@/lib/adminApi";
import { PRIMARY_FONTS, ACCENT_FONTS } from "@/lib/fontOptions";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TabCard = ({ title, subtitle, children }) => (
  <div className="bg-white border border-[#E5DFD3] p-8">
    {title && (
      <div className="mb-6">
        <h2 className="font-display text-2xl text-[#0A2342]">{title}</h2>
        {subtitle && <p className="text-sm text-[#5C636A] mt-1">{subtitle}</p>}
      </div>
    )}
    {children}
  </div>
);

const TabTrigger = ({ value, testId, label }) => (
  <TabsTrigger
    value={value}
    data-testid={testId}
    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0A2342] data-[state=active]:bg-transparent data-[state=active]:text-[#0A2342] uppercase text-xs tracking-[0.18em] px-4 py-3 whitespace-nowrap"
  >
    {label}
  </TabsTrigger>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { content, refresh } = useSiteContent();
  const [user, setUser] = useState(null);
  const [draft, setDraft] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [savingTab, setSavingTab] = useState("");

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login"); return; }
    me().then(setUser).catch(() => navigate("/admin/login"));
  }, [navigate]);

  useEffect(() => {
    if (content && !draft) {
      // Deep clone + add highlight_text mirror for pricing tiers (for round-trip UX)
      const cloned = JSON.parse(JSON.stringify(content));
      if (Array.isArray(cloned.pricing_tiers)) {
        cloned.pricing_tiers = cloned.pricing_tiers.map((t) => ({
          ...t,
          highlight_text: t.highlight_text != null
            ? t.highlight_text
            : (t.highlight ? "yes" : "no"),
        }));
      }
      setDraft(cloned);
    }
  }, [content, draft]);

  const loadBookings = async () => {
    try { setBookings(await listBookings()); }
    catch (err) {
      toast.error(formatErrorDetail(err?.response?.data?.detail) || "Could not load bookings");
    }
  };
  useEffect(() => { if (user) loadBookings(); }, [user]);

  if (!user || !draft) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center text-[#5C636A]">
        Loading...
      </div>
    );
  }

  const set = (key) => (value) => setDraft((d) => ({ ...d, [key]: value }));

  // Save with an explicit patch (avoids stale-closure bugs from setDraft+setTimeout).
  const saveExplicit = async (tab, patch) => {
    setSavingTab(tab);
    try {
      await updateSiteContent(patch);
      await refresh();
      // Sync local draft with the patch so subsequent edits start from the new state
      setDraft((d) => ({ ...d, ...patch }));
      toast.success("Changes saved.");
    } catch (err) {
      toast.error(formatErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSavingTab(""); }
  };

  const save = async (tab, fields) => {
    const patch = fields.reduce((acc, k) => ({ ...acc, [k]: draft[k] }), {});
    return saveExplicit(tab, patch);
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const handleDeleteBooking = async (id) => {
    if (!window.confirm("Delete this booking? This cannot be undone.")) return;
    try {
      await deleteBooking(id);
      setBookings((b) => b.filter((x) => x.id !== id));
      toast.success("Booking deleted");
    } catch (err) {
      toast.error(formatErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <main className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-[#0A2342] text-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <div>
            <div className="flex items-baseline gap-1">
              <span className="font-display text-2xl font-bold">{draft.logo_text_primary}</span>
              <span className="font-script text-3xl text-[#CBA358] -ml-1">{draft.logo_text_accent}</span>
              <span className="ml-3 text-[10px] uppercase tracking-[0.22em] text-[#CBA358]">Admin</span>
            </div>
            <p className="text-xs text-[#E5DFD3] mt-1">Signed in as {user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#CBA358] hover:text-white">
              <ExternalLink className="w-3.5 h-3.5" /> View site
            </a>
            <button onClick={doLogout} data-testid={ADMIN.logout} className="inline-flex items-center gap-2 border border-white/30 px-4 py-2 text-xs uppercase tracking-wider hover:bg-white/10">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-display text-4xl text-[#0A2342]">Dashboard</h1>
        <p className="mt-2 text-[#5C636A]">Every section is fully editable. Changes go live instantly.</p>

        <Tabs defaultValue="branding" className="mt-8">
          <div className="overflow-x-auto">
            <TabsList className="bg-transparent border-b border-[#E5DFD3] rounded-none w-max min-w-full justify-start gap-1 p-0 h-auto">
              <TabTrigger value="branding" testId={ADMIN.tabBranding} label="Branding" />
              <TabTrigger value="hero" testId={ADMIN.tabHero} label="Hero" />
              <TabTrigger value="about" testId={ADMIN.tabAbout} label="About" />
              <TabTrigger value="process" testId={ADMIN.tabProcess} label="Process" />
              <TabTrigger value="diff" testId={ADMIN.tabDiff} label="Why Us" />
              <TabTrigger value="founder" testId={ADMIN.tabFounder} label="Founder" />
              <TabTrigger value="testimonials" testId={ADMIN.tabTestimonials} label="Testimonials" />
              <TabTrigger value="booking-copy" testId={ADMIN.tabBookingCopy} label="Booking" />
              <TabTrigger value="faq" testId={ADMIN.tabFAQ} label="FAQ" />
              <TabTrigger value="footer" testId={ADMIN.tabFooter} label="Footer" />
              <TabTrigger value="gallery" testId={ADMIN.tabGallery} label="Gallery" />
              <TabTrigger value="pricing" testId={ADMIN.tabPricing} label="Pricing & Offers" />
              <TabTrigger value="bookings" testId={ADMIN.tabBookings} label={`Bookings (${bookings.length})`} />
            </TabsList>
          </div>

          {/* BRANDING */}
          <TabsContent value="branding" className="mt-8">
            <TabCard title="Branding & Contact" subtitle="Logo, name, tagline, contact info, free-slots, Google Form.">
              <div className="grid sm:grid-cols-2 gap-6">
                <ImageField
                  label="Logo (image, optional — max 1 MB)"
                  value={draft.logo_image}
                  onChange={set("logo_image")}
                  testId={ADMIN.uploadLogo}
                  previewClass="h-14 w-auto"
                />
                <Field label="Wordmark — first part" >
                  <Input value={draft.logo_text_primary} onChange={(e) => set("logo_text_primary")(e.target.value)} className={inputCls} placeholder="Invinci" />
                </Field>
                <Field label="Wordmark — accent (script style)">
                  <Input value={draft.logo_text_accent} onChange={(e) => set("logo_text_accent")(e.target.value)} className={inputCls} placeholder="Fy" />
                </Field>

                <Field label="Wordmark Font — first part" full>
                  <Select value={draft.logo_font_primary} onValueChange={set("logo_font_primary")}>
                    <SelectTrigger data-testid="font-primary-select" className="rounded-none border-x-0 border-t-0 border-b-2 border-[#E5DFD3] focus:ring-0 px-0 h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {PRIMARY_FONTS.map((f) => (
                        <SelectItem key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Wordmark Font — accent (script)" full>
                  <Select value={draft.logo_font_accent} onValueChange={set("logo_font_accent")}>
                    <SelectTrigger data-testid="font-accent-select" className="rounded-none border-x-0 border-t-0 border-b-2 border-[#E5DFD3] focus:ring-0 px-0 h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {ACCENT_FONTS.map((f) => (
                        <SelectItem key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <div className="sm:col-span-2 p-6 bg-[#FDFBF7] border border-[#E5DFD3]">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C636A] font-semibold mb-3">
                    Live Preview
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span
                      style={{ fontFamily: draft.logo_font_primary }}
                      className="text-5xl tracking-tight text-[#0A2342] font-bold leading-none"
                    >
                      {draft.logo_text_primary || "Invinci"}
                    </span>
                    <span
                      style={{ fontFamily: draft.logo_font_accent }}
                      className="text-6xl text-[#CBA358] leading-none -ml-1"
                    >
                      {draft.logo_text_accent || "Fy"}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-[#5C636A]">
                    Click <strong>Save changes</strong> below to apply across nav + footer.
                  </p>
                </div>

                <TextField label="Tagline" value={draft.tagline} onChange={set("tagline")} />
                <TextField label="Google Form URL (assessment)" value={draft.google_form_url} onChange={set("google_form_url")} placeholder="https://docs.google.com/forms/..." />
                <TextField label="Free Slot Limit" type="number" value={draft.free_slot_limit} onChange={(v) => set("free_slot_limit")(parseInt(v || "0", 10))} />
                <TextField label="Phone (display)" value={draft.contact_phone} onChange={set("contact_phone")} placeholder="+91 70557 44980" />
                <TextField label="Phone (dial format)" value={draft.contact_phone_tel} onChange={set("contact_phone_tel")} placeholder="+917055744980" />
                <TextField label="Instagram handle (no @)" value={draft.contact_instagram} onChange={(v) => set("contact_instagram")(v.replace(/^@/, ""))} />
                <TextField label="Email" type="email" value={draft.contact_email} onChange={set("contact_email")} />
              </div>
              <SaveBar
                onSave={() => save("branding", [
                  "logo_image","logo_text_primary","logo_text_accent",
                  "logo_font_primary","logo_font_accent","tagline",
                  "google_form_url","free_slot_limit",
                  "contact_phone","contact_phone_tel","contact_instagram","contact_email",
                ])}
                loading={savingTab === "branding"}
                testId={ADMIN.saveBranding}
              />
            </TabCard>
          </TabsContent>

          {/* HERO */}
          <TabsContent value="hero" className="mt-8">
            <TabCard title="Hero Section" subtitle="The top of your page — first impression matters.">
              <div className="grid sm:grid-cols-2 gap-6">
                <ImageField
                  label="Hero Photo — Primary (big image)"
                  value={draft.hero_image_primary}
                  onChange={set("hero_image_primary")}
                  testId={ADMIN.uploadHero1}
                  helpText="Stock photo"
                  previewClass="w-28 h-36"
                />
                <ImageField
                  label="Hero Photo — Overlay (smaller image)"
                  value={draft.hero_image_secondary}
                  onChange={set("hero_image_secondary")}
                  testId={ADMIN.uploadHero2}
                  helpText="Stock photo"
                  previewClass="w-28 h-36"
                />
                <TextAreaField label="Main Headline" value={draft.hero_headline} onChange={set("hero_headline")} rows={2} />
                <TextAreaField label="Subheadline / Description" value={draft.hero_subheadline} onChange={set("hero_subheadline")} rows={4} />
                <TextField label="Primary Button Text" value={draft.hero_cta_primary} onChange={set("hero_cta_primary")} />
                <TextField label="Secondary Button Text" value={draft.hero_cta_secondary} onChange={set("hero_cta_secondary")} />
                <StringListEditor
                  label="Hero Stats (small chips at bottom of hero)"
                  items={draft.hero_stats}
                  onChange={set("hero_stats")}
                  placeholder="5+ Years Mentoring"
                />
              </div>
              <SaveBar
                onSave={() => save("hero", [
                  "hero_image_primary","hero_image_secondary",
                  "hero_headline","hero_subheadline",
                  "hero_cta_primary","hero_cta_secondary","hero_stats",
                ])}
                loading={savingTab === "hero"}
                testId={ADMIN.saveHero}
              />
            </TabCard>
          </TabsContent>

          {/* ABOUT */}
          <TabsContent value="about" className="mt-8">
            <TabCard title="About / Science Section">
              <div className="grid sm:grid-cols-2 gap-6">
                <TextField label="Eyebrow" value={draft.about_eyebrow} onChange={set("about_eyebrow")} />
                <TextField label="Headline" value={draft.about_headline} onChange={set("about_headline")} />
                <TextAreaField label="Description" value={draft.about_description} onChange={set("about_description")} rows={4} />
                <ObjectListEditor
                  label="Feature Cards"
                  items={draft.about_features}
                  onChange={set("about_features")}
                  fields={[
                    { key: "title", label: "Title", placeholder: "DIMT Framework" },
                    { key: "body", label: "Description", textarea: true, full: true },
                  ]}
                  newItem={{ title: "", body: "" }}
                  addLabel="Add Feature"
                />
              </div>
              <SaveBar onSave={() => save("about", ["about_eyebrow","about_headline","about_description","about_features"])}
                loading={savingTab === "about"} testId={ADMIN.saveAbout} />
            </TabCard>
          </TabsContent>

          {/* PROCESS */}
          <TabsContent value="process" className="mt-8">
            <TabCard title="How It Works" subtitle="The 3-step process.">
              <div className="grid sm:grid-cols-2 gap-6">
                <TextField label="Eyebrow" value={draft.process_eyebrow} onChange={set("process_eyebrow")} />
                <TextField label="Headline" value={draft.process_headline} onChange={set("process_headline")} />
                <TextField label="Step 2 callout note" full value={draft.process_step2_note} onChange={set("process_step2_note")} />
                <ObjectListEditor
                  label="Process Steps"
                  items={draft.process_steps}
                  onChange={set("process_steps")}
                  fields={[
                    { key: "num", label: "Step number (e.g. 01)", placeholder: "01" },
                    { key: "title", label: "Step title" },
                    { key: "body", label: "Step description", textarea: true, full: true },
                  ]}
                  newItem={{ num: "", title: "", body: "" }}
                  addLabel="Add Step"
                />
              </div>
              <SaveBar onSave={() => save("process", ["process_eyebrow","process_headline","process_step2_note","process_steps"])}
                loading={savingTab === "process"} testId={ADMIN.saveProcess} />
            </TabCard>
          </TabsContent>

          {/* DIFFERENTIATORS */}
          <TabsContent value="diff" className="mt-8">
            <TabCard title="Why Choose Us" subtitle="The differentiator cards.">
              <div className="grid sm:grid-cols-2 gap-6">
                <TextField label="Eyebrow" value={draft.diff_eyebrow} onChange={set("diff_eyebrow")} />
                <TextField label="Headline" value={draft.diff_headline} onChange={set("diff_headline")} />
                <TextAreaField label="Side description (short)" value={draft.diff_description} onChange={set("diff_description")} rows={3} />
                <ObjectListEditor
                  label="Differentiator Cards"
                  items={draft.diff_items}
                  onChange={set("diff_items")}
                  fields={[
                    { key: "title", label: "Title" },
                    { key: "body", label: "Description", textarea: true, full: true },
                  ]}
                  newItem={{ title: "", body: "" }}
                  addLabel="Add Card"
                />
              </div>
              <SaveBar onSave={() => save("diff", ["diff_eyebrow","diff_headline","diff_description","diff_items"])}
                loading={savingTab === "diff"} testId={ADMIN.saveDiff} />
            </TabCard>
          </TabsContent>

          {/* FOUNDER */}
          <TabsContent value="founder" className="mt-8">
            <TabCard title="Founder's Diary">
              <div className="grid sm:grid-cols-2 gap-6">
                <ImageField label="Founder Photo (max 1 MB)" value={draft.founder_image} onChange={set("founder_image")}
                  testId={ADMIN.uploadFounder} helpText="Stock photo" previewClass="w-28 h-36" />
                <TextField label="Eyebrow" value={draft.founder_eyebrow} onChange={set("founder_eyebrow")} />
                <TextField label="Founder Name" value={draft.founder_name} onChange={set("founder_name")} />
                <TextField label="Subtitle / Credentials" full value={draft.founder_subtitle} onChange={set("founder_subtitle")} />
                <TextAreaField label="Mission Quote" value={draft.founder_mission_quote} onChange={set("founder_mission_quote")} rows={2} />
                <ParagraphListEditor label="Founder Story (paragraphs)" items={draft.founder_paragraphs} onChange={set("founder_paragraphs")} />
                <StringListEditor label='"Many Hats" badges' items={draft.founder_hats} onChange={set("founder_hats")} placeholder="Educator" />
              </div>
              <SaveBar onSave={() => save("founder", ["founder_eyebrow","founder_name","founder_subtitle","founder_image","founder_mission_quote","founder_paragraphs","founder_hats"])}
                loading={savingTab === "founder"} testId={ADMIN.saveFounder} />
            </TabCard>
          </TabsContent>

          {/* TESTIMONIALS */}
          <TabsContent value="testimonials" className="mt-8">
            <TabCard title="Testimonials">
              <div className="grid sm:grid-cols-2 gap-6">
                <TextField label="Eyebrow" value={draft.testimonials_eyebrow} onChange={set("testimonials_eyebrow")} />
                <TextField label="Headline" value={draft.testimonials_headline} onChange={set("testimonials_headline")} />
                <ObjectListEditor
                  label="Testimonials"
                  items={draft.testimonials}
                  onChange={set("testimonials")}
                  fields={[
                    { key: "name", label: "Name", placeholder: "Aanya Sharma" },
                    { key: "meta", label: "Meta (Class · City)", placeholder: "Class 12 · Delhi" },
                    { key: "body", label: "Quote", textarea: true, full: true, rows: 3 },
                  ]}
                  newItem={{ id: `t${Date.now()}`, name: "", meta: "", body: "" }}
                  addLabel="Add Testimonial"
                />
              </div>
              <SaveBar onSave={() => save("testimonials", ["testimonials_eyebrow","testimonials_headline","testimonials"])}
                loading={savingTab === "testimonials"} testId={ADMIN.saveTestimonials} />
            </TabCard>
          </TabsContent>

          {/* BOOKING COPY */}
          <TabsContent value="booking-copy" className="mt-8">
            <TabCard title="Booking Section Copy" subtitle="Labels & text shown around the booking form.">
              <div className="grid sm:grid-cols-2 gap-6">
                <TextField label="Eyebrow" value={draft.booking_eyebrow} onChange={set("booking_eyebrow")} />
                <TextField label="Headline" value={draft.booking_headline} onChange={set("booking_headline")} />
                <TextField label="Offer label" value={draft.booking_offer_label} onChange={set("booking_offer_label")} />
                <TextField label="Offer title" value={draft.booking_offer_title} onChange={set("booking_offer_title")} />
                <TextField label="Zoom session title" value={draft.booking_zoom_title} onChange={set("booking_zoom_title")} />
                <TextField label="Zoom session description" value={draft.booking_zoom_desc} onChange={set("booking_zoom_desc")} />
                <TextField label="In-person session title" value={draft.booking_inperson_title} onChange={set("booking_inperson_title")} />
                <TextField label="In-person session description" value={draft.booking_inperson_desc} onChange={set("booking_inperson_desc")} />
              </div>
              <SaveBar onSave={() => save("booking-copy", [
                "booking_eyebrow","booking_headline","booking_offer_label","booking_offer_title",
                "booking_zoom_title","booking_zoom_desc","booking_inperson_title","booking_inperson_desc",
              ])}
                loading={savingTab === "booking-copy"} testId={ADMIN.saveBookingCopy} />
            </TabCard>
          </TabsContent>

          {/* FAQ */}
          <TabsContent value="faq" className="mt-8">
            <TabCard title="FAQ">
              <div className="grid sm:grid-cols-2 gap-6">
                <TextField label="Eyebrow" value={draft.faq_eyebrow} onChange={set("faq_eyebrow")} />
                <TextField label="Headline" value={draft.faq_headline} onChange={set("faq_headline")} />
                <ObjectListEditor
                  label="FAQ Items"
                  items={draft.faq_items}
                  onChange={set("faq_items")}
                  fields={[
                    { key: "q", label: "Question", full: true },
                    { key: "a", label: "Answer", textarea: true, full: true, rows: 4 },
                  ]}
                  newItem={{ q: "", a: "" }}
                  addLabel="Add FAQ"
                />
              </div>
              <SaveBar onSave={() => save("faq", ["faq_eyebrow","faq_headline","faq_items"])}
                loading={savingTab === "faq"} testId={ADMIN.saveFAQ} />
            </TabCard>
          </TabsContent>

          {/* FOOTER */}
          <TabsContent value="footer" className="mt-8">
            <TabCard title="Footer">
              <div className="grid sm:grid-cols-2 gap-6">
                <TextAreaField label="Footer description" value={draft.footer_description} onChange={set("footer_description")} rows={3} />
                <TextField label="Closing tagline (script font)" value={draft.footer_closing_quote} onChange={set("footer_closing_quote")} />

                <Field label="Closing tagline font" full>
                  <Select value={draft.footer_closing_quote_font} onValueChange={set("footer_closing_quote_font")}>
                    <SelectTrigger data-testid="footer-quote-font-select" className="rounded-none border-x-0 border-t-0 border-b-2 border-[#E5DFD3] focus:ring-0 px-0 h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {ACCENT_FONTS.map((f) => (
                        <SelectItem key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <div className="sm:col-span-2 p-6 bg-[#0A2342]">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#CBA358] font-semibold mb-3">
                    Live Preview
                  </div>
                  <div
                    style={{ fontFamily: draft.footer_closing_quote_font }}
                    className="text-3xl text-[#CBA358]"
                  >
                    {draft.footer_closing_quote || "Let's make the difference, together."}
                  </div>
                </div>
              </div>
              <SaveBar onSave={() => save("footer", ["footer_description","footer_closing_quote","footer_closing_quote_font"])}
                loading={savingTab === "footer"} testId={ADMIN.saveFooter} />
            </TabCard>
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery" className="mt-8">
            <TabCard title="Gallery" subtitle="Photos from sessions, students, events. Shows between Founder and Testimonials. Max 1 MB per image.">
              <div className="grid sm:grid-cols-2 gap-6">
                <TextField label="Eyebrow" value={draft.gallery_eyebrow} onChange={set("gallery_eyebrow")} />
                <TextField label="Headline" value={draft.gallery_headline} onChange={set("gallery_headline")} />
                <TextAreaField label="Description (optional)" value={draft.gallery_description} onChange={set("gallery_description")} rows={3} />
                <GalleryEditor items={draft.gallery_items} onChange={set("gallery_items")} />
              </div>
              <SaveBar
                onSave={() => save("gallery", ["gallery_eyebrow", "gallery_headline", "gallery_description", "gallery_items"])}
                loading={savingTab === "gallery"}
                testId={ADMIN.saveGallery}
              />
            </TabCard>
          </TabsContent>

          {/* PRICING */}
          <TabsContent value="pricing" className="mt-8">
            <TabCard title="Pricing & Offers" subtitle="Editable pricing tiers + an eye-catching offer sticker. Untoggle to hide the whole section.">
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Show this section">
                  <label className="inline-flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!draft.pricing_enabled}
                      onChange={(e) => set("pricing_enabled")(e.target.checked)}
                      className="w-5 h-5 accent-[#0A2342]"
                      data-testid="pricing-enabled-toggle"
                    />
                    <span className="text-sm text-[#0A2342]">
                      {draft.pricing_enabled ? "Section is VISIBLE on the site" : "Section is HIDDEN"}
                    </span>
                  </label>
                </Field>
                <TextField label="Eyebrow" value={draft.pricing_eyebrow} onChange={set("pricing_eyebrow")} />
                <TextField label="Headline" value={draft.pricing_headline} onChange={set("pricing_headline")} full />
                <TextAreaField label="Description (optional)" value={draft.pricing_description} onChange={set("pricing_description")} rows={2} />

                <div className="sm:col-span-2 border-t border-[#E5DFD3] pt-6 mt-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#CBA358] font-semibold mb-4">
                    Offer Sticker (the highlighted badge)
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="Show sticker">
                      <label className="inline-flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!draft.pricing_sticker_visible}
                          onChange={(e) => set("pricing_sticker_visible")(e.target.checked)}
                          className="w-5 h-5 accent-[#0A2342]"
                          data-testid="pricing-sticker-toggle"
                        />
                        <span className="text-sm text-[#0A2342]">
                          {draft.pricing_sticker_visible ? "Sticker VISIBLE" : "Sticker HIDDEN"}
                        </span>
                      </label>
                    </Field>
                    <TextField
                      label="Sticker main text (e.g. FLAT 50% OFF)"
                      value={draft.pricing_sticker_text}
                      onChange={set("pricing_sticker_text")}
                      placeholder="FLAT 50% OFF"
                    />
                    <TextField
                      label="Sticker subtext (e.g. Limited time)"
                      value={draft.pricing_sticker_subtext}
                      onChange={set("pricing_sticker_subtext")}
                      placeholder="Limited time — launch offer"
                      full
                    />
                  </div>
                </div>

                <div className="sm:col-span-2 border-t border-[#E5DFD3] pt-6 mt-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#CBA358] font-semibold mb-4">
                    Pricing Tiers
                  </div>
                  <ObjectListEditor
                    label=""
                    items={draft.pricing_tiers}
                    onChange={set("pricing_tiers")}
                    fields={[
                      { key: "title", label: "Title (e.g. Zoom 1:1 Session)" },
                      { key: "price_text", label: "Price (e.g. ₹1,499)" },
                      { key: "original_price", label: "Original price (strike-through, optional)" },
                      { key: "description", label: "Short description", full: true, textarea: true, rows: 2 },
                      { key: "cta_text", label: "Button text (e.g. Book Zoom Session)" },
                      { key: "highlight_text", label: 'Mark as "Most Picked"? (type yes/no)', placeholder: "no" },
                    ]}
                    newItem={{ id: `p${Date.now()}`, title: "", price_text: "", original_price: "", description: "", cta_text: "Book Now", highlight: false }}
                    addLabel="Add Pricing Tier"
                  />
                  <p className="text-xs text-[#5C636A] mt-3 italic">
                    Tip: For per-tier feature lists (the bullet points), edit them after saving — they keep their current values. To toggle &ldquo;Most Picked&rdquo; highlight, set the field above to yes/no.
                  </p>
                </div>

                <TextAreaField label="Footnote below tiers" value={draft.pricing_footnote} onChange={set("pricing_footnote")} rows={2} />
              </div>

              {/* Per-tier features editor */}
              {(draft.pricing_tiers || []).length > 0 && (
                <div className="mt-8 border-t border-[#E5DFD3] pt-6">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#CBA358] font-semibold mb-4">
                    Features per Tier (the ✓ bullet points)
                  </div>
                  <div className="space-y-6">
                    {(draft.pricing_tiers || []).map((t, ti) => (
                      <div key={t.id || ti} className="border border-[#E5DFD3] p-4 bg-[#FDFBF7]">
                        <div className="font-display text-lg text-[#0A2342] mb-3">
                          {t.title || `Tier #${ti + 1}`}
                        </div>
                        <StringListEditor
                          label="Features (one per line)"
                          items={t.features}
                          onChange={(newList) => {
                            const tiers = [...(draft.pricing_tiers || [])];
                            tiers[ti] = { ...tiers[ti], features: newList };
                            set("pricing_tiers")(tiers);
                          }}
                          placeholder="DIMT + Psychometric Assessment"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <SaveBar
                onSave={() => {
                  const normalized = (draft.pricing_tiers || []).map((t) => ({
                    ...t,
                    highlight:
                      t.highlight_text != null
                        ? /^(y|yes|true|1)$/i.test(String(t.highlight_text).trim())
                        : !!t.highlight,
                  }));
                  const patch = {
                    pricing_enabled: draft.pricing_enabled,
                    pricing_eyebrow: draft.pricing_eyebrow,
                    pricing_headline: draft.pricing_headline,
                    pricing_description: draft.pricing_description,
                    pricing_sticker_visible: draft.pricing_sticker_visible,
                    pricing_sticker_text: draft.pricing_sticker_text,
                    pricing_sticker_subtext: draft.pricing_sticker_subtext,
                    pricing_tiers: normalized,
                    pricing_footnote: draft.pricing_footnote,
                  };
                  saveExplicit("pricing", patch);
                }}
                loading={savingTab === "pricing"}
                testId={ADMIN.savePricing}
              />
            </TabCard>
          </TabsContent>

          {/* BOOKINGS */}
          <TabsContent value="bookings" className="mt-8">
            <div className="mb-4 flex justify-end">
              <button
                type="button"
                data-testid={ADMIN.exportBookingsCsv}
                onClick={async () => {
                  try {
                    const res = await adminAxios.get("/admin/bookings/export.csv", { responseType: "blob" });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "invincify-bookings.csv";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                    toast.success("CSV downloaded");
                  } catch (err) {
                    toast.error(formatErrorDetail(err?.response?.data?.detail) || "Export failed");
                  }
                }}
                className="inline-flex items-center gap-2 bg-[#0A2342] text-white px-4 py-2 text-xs uppercase tracking-wider hover:bg-[#173B63]"
              >
                <Download className="w-3.5 h-3.5" /> Download CSV
              </button>
            </div>
            <div className="bg-white border border-[#E5DFD3] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4EFE6] text-[#0A2342] text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-5 py-3">When</th>
                      <th className="text-left px-5 py-3">Name</th>
                      <th className="text-left px-5 py-3">Email</th>
                      <th className="text-left px-5 py-3">Phone</th>
                      <th className="text-left px-5 py-3">Class</th>
                      <th className="text-left px-5 py-3">Session</th>
                      <th className="text-left px-5 py-3">Package</th>
                      <th className="text-left px-5 py-3">Free?</th>
                      <th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.length === 0 && (
                      <tr><td colSpan="9" className="text-center py-10 text-[#5C636A]">No bookings yet.</td></tr>
                    )}
                    {bookings.map((b, i) => (
                      <tr key={b.id} className="border-t border-[#E5DFD3] hover:bg-[#FDFBF7]">
                        <td className="px-5 py-3 text-[#5C636A] text-xs">{new Date(b.created_at).toLocaleString()}</td>
                        <td className="px-5 py-3 font-medium text-[#0A2342]">{b.name}</td>
                        <td className="px-5 py-3"><a className="text-[#0A2342] underline" href={`mailto:${b.email}`}>{b.email}</a></td>
                        <td className="px-5 py-3"><a className="text-[#0A2342] underline" href={`tel:${b.phone}`}>{b.phone}</a></td>
                        <td className="px-5 py-3 text-[#5C636A]">{b.grade || "—"}</td>
                        <td className="px-5 py-3 text-[#5C636A] capitalize">{b.session_type.replace("_"," ")}</td>
                        <td className="px-5 py-3 text-[#0A2342] text-xs font-medium">{b.package || "—"}</td>
                        <td className="px-5 py-3">
                          {b.is_free_slot ? (
                            <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#CBA358] text-[#0A2342] font-bold">Free</span>
                          ) : (
                            <span className="text-[#5C636A] text-xs">Paid</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button data-testid={ADMIN.deleteBooking(i)} onClick={() => handleDeleteBooking(b.id)} className="text-[#5C636A] hover:text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
};

export default AdminDashboard;
